/**
 * Reading the plan's open questions, and the plan row they come from.
 *
 * The ticket sheet answers them (`ticket/QuestionCards.tsx`,
 * `ticket/StatedDecisions.tsx`); this module only reads them. Three sources,
 * in this order, and the order is the honest one:
 *
 *   1. `gate.payload.planQuestions`, additive and absent today. Read first so the
 *      moment the gate carries structured questions, this block prefers them
 *      without another line of frontend change.
 *   2. the plan row's `questions` (`GET /api/work-items/:id/plans`), the REAL
 *      structured source today. The mapper normalizes the JSONB column into
 *      `PlanQuestion[]` (text · recommendation · alternatives, each option
 *      carrying its own one-line reason), so recommendations and alternatives
 *      here are the planner's own, never inferred.
 *   3. the page's "Open questions" markdown, the fallback for a plan row this
 *      reader cannot load, and for rows written before the structured shape
 *      existed. The parse is the exact inverse of the backend renderer
 *      (`- question`, then `  - Recommended: answer Why: reason` / `  - Or: …`),
 *      so it recovers what was written rather than guessing at prose.
 *
 * What this module deliberately does NOT do: invent options. A question with no
 * recommendation reads as a question with no recommendation. Manufacturing two
 * plausible-looking options under a question the planner had no view on would
 * be the surface lying in the one place it is most expensive.
 */
import { useEffect, useState } from "react";
import {
  gatePlanQuestionEntries,
  planQuestionId,
  type BuilderPlan,
  type PlanCheck,
  type PlanQuestionCheckedLine,
  type PlanQuestionKind,
  type PlanQuestionOption,
  type WorkItemGate,
} from "@yagni/shared";
import { sectionLines } from "./planMarkdown.js";

/** The section title the backend page renderer writes. */
export const PLAN_QUESTIONS_SECTION = "Open questions";

/** One question, with the id the staged answers are keyed by. */
export interface PlanQuestionRow {
  id: string;
  text: string;
  recommendation: PlanQuestionOption | null;
  alternatives: PlanQuestionOption[];
  /** A fact can be looked up; a decision is a person's call. Absent: unknown. */
  kind?: PlanQuestionKind;
  /** A fact only: the source (a connector id) that would answer it and the Team lacks. */
  needsTool?: string;
  /** A fact only: what Reeve read for it, and what it could not. */
  checked?: PlanQuestionCheckedLine[];
  /** A decision only: it cannot be undone once built, so a person always answers it. */
  oneWay?: boolean;
  /** A fact only: its answer rests on a source Reeve could not read, so a person answers it. */
  unreadSource?: boolean;
}

/**
 * An answer a browser kept before answers were saved on the server (until
 * 2026-11-02), read once to move it there (`stagedPlanAnswers.ts`).
 */
export interface StagedAnswer {
  questionId: string;
  question: string;
  answer: string;
  why: string;
  /** Skip took the recommendation: the person let Reeve choose. */
  skipped?: true;
}

/* ── reading the questions ─────────────────────────────────────────────────── */

/** At most two alternatives, the same cap the backend normalizer applies. */
const MAX_ALTERNATIVES = 2;

// The ids here come from @yagni/shared's `planQuestionId` — the SAME
// derivation the backend's cross-check imports, so the two sides of the wire
// can never drift apart and silently drop every staged answer.

function readOption(value: unknown): PlanQuestionOption | null {
  if (typeof value === "string") {
    const answer = value.trim();
    return answer ? { answer, why: "" } : null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const answer = typeof row.answer === "string" ? row.answer.trim() : "";
  if (!answer) return null;
  const why = typeof row.why === "string" ? row.why.trim() : "";
  const planChange = readPlanChange(row.planChange);
  return { answer, why, ...(planChange ? { planChange } : {}) };
}

/** What picking the option changes, as the gate carries it. Total: anything unreadable is null. */
function readPlanChange(value: unknown): PlanQuestionOption["planChange"] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const strings = (entry: unknown): string[] =>
    Array.isArray(entry)
      ? entry.filter((item): item is string => typeof item === "string" && item.trim() !== "")
      : [];
  const records = (entry: unknown): Array<Record<string, unknown>> =>
    Array.isArray(entry)
      ? entry.filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
      : [];
  const addFiles = strings(row.addFiles);
  const addSteps = records(row.addSteps)
    .filter((step) => typeof step.description === "string")
    .map((step) => ({
      description: String(step.description),
      ...(typeof step.title === "string" ? { title: step.title } : {}),
    }));
  const editSteps = records(row.editSteps)
    .filter((edit) => typeof edit.step === "number" && typeof edit.description === "string")
    .map((edit) => ({
      step: Number(edit.step),
      fromText: typeof edit.fromText === "string" ? edit.fromText : "",
      description: String(edit.description),
    }));
  const addChecks = strings(row.addChecks);
  const addAcceptanceCriteria = strings(row.addAcceptanceCriteria);
  const change = {
    ...(addFiles.length ? { addFiles } : {}),
    ...(addSteps.length ? { addSteps } : {}),
    ...(editSteps.length ? { editSteps } : {}),
    ...(addChecks.length ? { addChecks } : {}),
    ...(addAcceptanceCriteria.length ? { addAcceptanceCriteria } : {}),
  };
  return Object.keys(change).length ? change : null;
}

function readAlternatives(value: unknown): PlanQuestionOption[] {
  if (!Array.isArray(value)) return [];
  const out: PlanQuestionOption[] = [];
  for (const entry of value) {
    const option = readOption(entry);
    if (option) out.push(option);
    if (out.length === MAX_ALTERNATIVES) break;
  }
  return out;
}

/**
 * One entry to one row, tolerant of a shape written by a server that knows more
 * than this build does (the same rule `readPlanAmendments` follows): an entry
 * with no usable question text is dropped rather than rendered half-blank, and a
 * question we can phrase but cannot advise on is still worth asking.
 */
function readQuestion(value: unknown, index: number): PlanQuestionRow | null {
  if (typeof value === "string") {
    const text = value.trim();
    return text
      ? { id: planQuestionId(text, index), text, recommendation: null, alternatives: [] }
      : null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const raw =
    typeof row.text === "string" ? row.text : typeof row.question === "string" ? row.question : "";
  const text = raw.trim();
  if (!text) return null;
  const kind = row.kind === "fact" || row.kind === "decision" ? row.kind : null;
  // The source and the checked lines describe a fact; on anything else they
  // are a server that wrote more than it meant, and are not drawn.
  const needsTool =
    kind === "fact" && typeof row.needsTool === "string" ? row.needsTool.trim() : "";
  const checked = kind === "fact" ? readCheckedLines(row.checked) : [];
  const oneWay = kind === "decision" && row.oneWay === true;
  const unreadSource = kind === "fact" && row.unreadSource === true;
  return {
    id: typeof row.id === "string" && row.id.trim() ? row.id.trim() : planQuestionId(text, index),
    text,
    recommendation: readOption(row.recommendation),
    alternatives: readAlternatives(row.alternatives),
    ...(kind ? { kind } : {}),
    ...(oneWay ? { oneWay } : {}),
    ...(needsTool ? { needsTool } : {}),
    ...(checked.length ? { checked } : {}),
    ...(unreadSource ? { unreadSource } : {}),
  };
}

function readCheckedLines(value: unknown): PlanQuestionCheckedLine[] {
  if (!Array.isArray(value)) return [];
  const out: PlanQuestionCheckedLine[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const line = entry as Record<string, unknown>;
    const text = typeof line.text === "string" ? line.text.trim() : "";
    if (!text || (line.status !== "checked" && line.status !== "not_checked")) continue;
    out.push({ status: line.status, text });
  }
  return out;
}

export function readPlanQuestionList(value: unknown): PlanQuestionRow[] {
  if (!Array.isArray(value)) return [];
  const out: PlanQuestionRow[] = [];
  value.forEach((entry, index) => {
    const row = readQuestion(entry, index);
    if (row) out.push(row);
  });
  return out;
}

/**
 * The questions the open gate carries, when it carries any (additive payload):
 * its `planQuestions`, else a one-attempt lane gate's own `questions`, the
 * list the approval holds the answers against (`gatePlanQuestionEntries`).
 */
export function readGatePlanQuestions(gate: WorkItemGate | null): PlanQuestionRow[] {
  return readPlanQuestionList(gatePlanQuestionEntries(gate?.payload));
}

/**
 * The facts Reeve settled by reading with the Team's connected tools, off the
 * plan gate's payload (`payload.planChecks`). A fact settled this way is not a
 * question, so it prints in "Checks Reeve ran" rather than vanishing. An entry
 * with no text is dropped; absent reads as none.
 */
export function readGatePlanChecks(gate: WorkItemGate | null): PlanCheck[] {
  if (!gate || gate.kind !== "approve_execution_plan") return [];
  const raw = (gate.payload as { planChecks?: unknown } | null)?.planChecks;
  if (!Array.isArray(raw)) return [];
  const out: PlanCheck[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const check = entry as Record<string, unknown>;
    const text = typeof check.text === "string" ? check.text.trim() : "";
    if (!text) continue;
    out.push({ tool: typeof check.tool === "string" ? check.tool : "", text });
  }
  return out;
}

/**
 * Reeve's read off the SAME plan gate's payload (`payload.reeveRead`, written
 * by the planning surface from the plan's own final check, ADR-0049).
 *
 * It lives beside `readGatePlanQuestions` because it reads the same object for
 * the same reason. `watchSet` is a pair of counted facts or it is nothing: a
 * half-written one (a number with no list, a list with no number) is read as
 * absent rather than reported as a count somebody made. A send-back count an
 * older gate still carries is ignored; the loop that wrote it is retired.
 */
export interface ReeveRead {
  verdict: "changes_requested" | "approved";
  rationale: string;
  watchSet: { weighed: number; raised: string[] } | null;
}

export function readGateReeveRead(gate: WorkItemGate | null): ReeveRead | null {
  if (!gate || gate.kind !== "approve_execution_plan") return null;
  const raw = (gate.payload as { reeveRead?: unknown } | null)?.reeveRead;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.verdict !== "changes_requested" && row.verdict !== "approved") return null;
  const rationale = typeof row.rationale === "string" ? row.rationale.trim() : "";
  const setRaw = row.watchSet;
  let watchSet: ReeveRead["watchSet"] = null;
  if (setRaw && typeof setRaw === "object" && !Array.isArray(setRaw)) {
    const set = setRaw as Record<string, unknown>;
    const weighed = typeof set.weighed === "number" ? set.weighed : null;
    const raised = Array.isArray(set.raised)
      ? set.raised.filter(
          (entry): entry is string => typeof entry === "string" && entry.trim() !== "",
        )
      : null;
    if (weighed != null && raised != null) watchSet = { weighed, raised };
  }
  return { verdict: row.verdict, rationale, watchSet };
}

const OPTION_LINE = /^\s*[-*]\s*(Recommended|Or)\s*:\s*(.+)$/i;
const QUESTION_LINE = /^[-*]\s+(.+)$/;

/**
 * Parse the page's "Open questions" section back into rows.
 *
 * The inverse of `renderQuestionsSection` in `workItems/planPage.ts`: a top
 * level bullet is a question, and an indented `Recommended:` / `Or:` bullet is
 * one of its options, with "Why:" separating the answer from its reason. Prose
 * that fits neither shape is skipped rather than half-read.
 */
export function parsePlanQuestionsMarkdown(pageContent: string): PlanQuestionRow[] {
  const lines = sectionLines(pageContent, PLAN_QUESTIONS_SECTION);
  const out: PlanQuestionRow[] = [];
  for (const line of lines) {
    const indented = /^(\s{2,}|\t)/.test(line);
    const option = indented ? OPTION_LINE.exec(line) : null;
    if (option && out.length > 0) {
      const current = out[out.length - 1]!;
      const [answerPart, ...whyParts] = option[2]!.split(/\s+Why:\s+/i);
      const parsed: PlanQuestionOption = {
        answer: (answerPart ?? "").trim(),
        why: whyParts.join(" Why: ").trim(),
      };
      if (!parsed.answer) continue;
      if (option[1]!.toLowerCase() === "recommended" && !current.recommendation) {
        current.recommendation = parsed;
      } else if (current.alternatives.length < MAX_ALTERNATIVES) {
        current.alternatives.push(parsed);
      }
      continue;
    }
    if (indented) continue;
    const question = QUESTION_LINE.exec(line.trim());
    if (!question) continue;
    const text = question[1]!.trim();
    if (!text) continue;
    out.push({
      id: planQuestionId(text, out.length),
      text,
      recommendation: null,
      alternatives: [],
    });
  }
  return out;
}

/**
 * The questions to render: the structured rows the pane already read (gate
 * payload or plan row), else the page's own markdown.
 *
 * The first non-empty source wins outright. Merging them would mean showing a
 * question twice under two ids, and the ids are what the staged answers are
 * keyed by.
 */
export function readPlanQuestions(
  structured: PlanQuestionRow[] | null | undefined,
  pageContent: string | null | undefined,
): PlanQuestionRow[] {
  if (structured && structured.length > 0) return structured;
  return pageContent ? parsePlanQuestionsMarkdown(pageContent) : [];
}

/**
 * Which plan row's questions to show, out of every row the route returned.
 *
 * The row named by `planId` when there is one (the gate's plan, else the
 * item's latest). With no name, or a name the list does not carry, the newest
 * revision by version, the same rule `readRevisions` in `planRevisions.tsx`
 * applies: never the route's first row, whose position says nothing about
 * which plan is current.
 */
export function pickPlanRow<T extends { id?: unknown; version?: unknown }>(
  list: readonly T[],
  planId: string | null,
): T | undefined {
  const named = planId ? list.find((plan) => plan.id === planId) : undefined;
  if (named) return named;
  let newest: T | undefined;
  let newestVersion = -Infinity;
  for (const row of list) {
    const version = typeof row.version === "number" ? row.version : -Infinity;
    if (newest === undefined || version > newestVersion) {
      newest = row;
      newestVersion = version;
    }
  }
  return newest;
}

/** The facts the folded plan line reads off the plan row, before any fold opens. */
export type PlanRowFacts = Partial<
  Pick<
    BuilderPlan,
    | "id"
    | "version"
    | "approach"
    | "riskLevel"
    | "steps"
    | "filesToChange"
    | "acceptanceCriteria"
    | "confirmedAt"
    | "createdAt"
  >
> & { questions?: unknown };

/**
 * The plan row: `GET /api/work-items/:id/plans`, picked by `pickPlanRow`.
 *
 * A shipped contract, not a new route. The folded plan line needs the plan's
 * shape (its approach, risk and counts) before a reader opens it, and the
 * questions read from the same row. Null while loading, when disabled, or
 * when the read failed (said once on the console). `versionId` is the plan's
 * newest version: an applied answer or an accepted Ask change rewrites the
 * same row under the same id, so a new version re-reads it.
 */
export function usePlanRow(
  workItemId: string,
  planId: string | null,
  enabled: boolean,
  versionId: string | null = null,
): PlanRowFacts | null {
  const [row, setRow] = useState<PlanRowFacts | null>(null);
  useEffect(() => {
    if (!enabled) {
      setRow(null);
      return;
    }
    let cancelled = false;
    // A read that fails is said so, once, on the console: the block falls
    // back to the page's own markdown (see `readPlanQuestions`), which is the
    // right thing to show, but "the row could not be read" and "the plan
    // asked nothing" must not be the same silence for whoever is debugging.
    const unreadable = (why: string) => {
      console.warn(`plan questions: could not read the plan row for ${workItemId}: ${why}`);
      if (!cancelled) setRow(null);
    };
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/plans`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          unreadable(`HTTP ${res.status}`);
          return;
        }
        return (res.json() as Promise<unknown>).then((body) => {
          if (cancelled) return;
          const list = Array.isArray(body) ? (body as PlanRowFacts[]) : [];
          setRow(pickPlanRow(list, planId) ?? null);
        });
      })
      .catch((err: unknown) => {
        unreadable(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, planId, enabled, versionId]);
  return row;
}
